import { DistributionReasonsByAccountTypeAndAgeCategory } from '../constants/transferConstants.js';
import { yup, strictObjectSchema, isInteger, isSingleDigitDecimal, ignoredTestName } from './validationLib.js';
import { ErrorCode, SystemValidationError } from '../constants/errorCodes.js';
import { InvestmentAccountStatus } from '../constants/investmentAccountConstants.js';
import { daysOfMonth } from '../util/time.js';

const { REQUIRED_SYSTEM_HANDLED, UNEXPECTED_SYSTEM_HANDLED, INVALID_ACCOUNT_STATE_FOR_REQUEST } = SystemValidationError;
const { ONBOARDING, ACTIVE } = InvestmentAccountStatus;

const MIN_TRANSFER_DOLLARS = 50;
const MAX_WITHHOLDING_PERCENT = 50;

const linkedAccountIdSchema = yup.string().label('linkedAccountId').required(REQUIRED_SYSTEM_HANDLED);

const checkValidLinkedAccountId = [
  ignoredTestName,
  UNEXPECTED_SYSTEM_HANDLED,
  function ({ linkedAccountId }) {
    const { linkedAccountIds } = this.options.context();
    const isValid = linkedAccountIds.includes(linkedAccountId);
    if (!isValid) {
      return this.createError({ path: 'linkedAccountId', message: UNEXPECTED_SYSTEM_HANDLED });
    }
    return true;
  },
];

const transferRequestIdSchema = yup
  .string()
  .label('transferRequestId')
  .required(REQUIRED_SYSTEM_HANDLED)
  .test(ignoredTestName, UNEXPECTED_SYSTEM_HANDLED, function (transferRequestId) {
    const { pendingTransferRequestIds } = this.options.context();
    return pendingTransferRequestIds.includes(transferRequestId);
  });

const recurringDepositIdSchema = yup
  .string()
  .label('recurringDepositId')
  .required(REQUIRED_SYSTEM_HANDLED)
  .test(ignoredTestName, UNEXPECTED_SYSTEM_HANDLED, function (recurringDepositId) {
    const { recurringDepositIds } = this.options.context();
    return recurringDepositIds.includes(recurringDepositId);
  });

const makeAmountSchema = (maxAmountTest) =>
  yup
    .number()
    .label('transferAmount')
    .required('required')
    .min(MIN_TRANSFER_DOLLARS, 'minTransferAmount')
    .test(ignoredTestName, 'validationMessage', isInteger)
    .test(ignoredTestName, 'maxTransferAmount', maxAmountTest);

const makeWitholdingPercentSchema = (label) =>
  yup
    .number()
    .label(label)
    .required('validPercent')
    .test(ignoredTestName, 'validPercent', isSingleDigitDecimal)
    .min(0, 'validPercent')
    .max(MAX_WITHHOLDING_PERCENT, 'validPercent');

const strictObjectSchemaForAccountStatus = (schema, validAccountStatuses) =>
  strictObjectSchema(schema).test(ignoredTestName, INVALID_ACCOUNT_STATE_FOR_REQUEST, function () {
    const { accountStatus } = this.options.context();
    return validAccountStatuses.includes(accountStatus);
  });

const createIncomingTransferRequestClientSchema = strictObjectSchemaForAccountStatus(
  {
    linkedAccountId: linkedAccountIdSchema,

    amount: makeAmountSchema(function (amount) {
      const { depositOptions } = this.options.context();
      const { taxYear } = this.parent;

      const depositOption = depositOptions.find((o) => o.taxYear === taxYear);
      if (!depositOption) {
        // there is something missing (e.g. taxYear) that needs to get resolved first before we validate.
        // assumes other validations will fail
        return true;
      }

      return amount <= depositOption.maxContribution;
    }),

    taxYear: yup
      .number()
      .label('taxYear')
      .required('required')
      .test(ignoredTestName, 'taxValidationMessage', function (taxYear) {
        const { depositOptions } = this.options.context();
        const validTaxYears = depositOptions.map((o) => o.taxYear);
        return validTaxYears.includes(taxYear);
      }),
  },
  [ONBOARDING, ACTIVE],
);

const createIncomingTransferRequestSchema = createIncomingTransferRequestClientSchema.test(
  ...checkValidLinkedAccountId,
);

const createOutgoingTransferRequestClientSchema = strictObjectSchemaForAccountStatus(
  {
    linkedAccountId: linkedAccountIdSchema,

    amount: makeAmountSchema(function (amount) {
      const { latestBalance } = this.options.context();
      return amount <= latestBalance;
    }),

    distributionReason: yup
      .string()
      .label('transferDistributionReason')
      .required('required')
      .test(ignoredTestName, UNEXPECTED_SYSTEM_HANDLED, function (distributionReason) {
        const { accountType, distributionAgeCategory } = this.options.context();
        const validReasons =
          DistributionReasonsByAccountTypeAndAgeCategory[accountType]?.[distributionAgeCategory] ?? [];

        return validReasons.includes(distributionReason);
      }),

    federalWithholdingPercent: makeWitholdingPercentSchema('transferFederalWithholdingPercent'),
    stateWithholdingPercent: makeWitholdingPercentSchema('transferStateWithholdingPercent'),
  },
  [ACTIVE],
);

const createOutgoingTransferRequestSchema = createOutgoingTransferRequestClientSchema.test(
  ...checkValidLinkedAccountId,
);

const cancelTransferRequestSchema = strictObjectSchemaForAccountStatus(
  {
    transferRequestId: transferRequestIdSchema,
  },
  [ACTIVE],
);

const baseRecurringDepositSchema = {
  linkedAccountId: linkedAccountIdSchema,

  amount: makeAmountSchema(function (amount) {
    const { depositOptions } = this.options.context();
    const maxTaxYear = Math.max(...depositOptions.map(({ taxYear }) => taxYear));
    const depositOption = depositOptions.find((o) => o.taxYear === maxTaxYear);
    return amount <= depositOption.maxContribution;
  }),

  dayOfMonth: yup.number().label('dayOfMonth').required('required').oneOf(daysOfMonth, 'dayValidationMessage'),
};

const createRecurringDepositClientSchema = strictObjectSchemaForAccountStatus(
  {
    ...baseRecurringDepositSchema,
  },
  [ONBOARDING, ACTIVE],
).test(ignoredTestName, ErrorCode.ACCOUNT_ALREADY_HAS_RECURRING_DEPOSIT, function () {
  const { accountStatus, recurringDepositIds } = this.options.context();
  return accountStatus === ONBOARDING || recurringDepositIds.length === 0;
});

const createRecurringDepositSchema = createRecurringDepositClientSchema.test(...checkValidLinkedAccountId);

const updateRecurringDepositSchema = strictObjectSchemaForAccountStatus(
  {
    recurringDepositId: recurringDepositIdSchema,
    ...baseRecurringDepositSchema,
  },
  [ACTIVE],
).test(...checkValidLinkedAccountId);

const cancelRecurringDepositSchema = strictObjectSchemaForAccountStatus(
  {
    recurringDepositId: recurringDepositIdSchema,
  },
  [ACTIVE],
);

export {
  createIncomingTransferRequestClientSchema,
  createIncomingTransferRequestSchema,
  createOutgoingTransferRequestClientSchema,
  createOutgoingTransferRequestSchema,
  cancelTransferRequestSchema,
  createRecurringDepositClientSchema,
  createRecurringDepositSchema,
  updateRecurringDepositSchema,
  cancelRecurringDepositSchema,
  MIN_TRANSFER_DOLLARS,
  MAX_WITHHOLDING_PERCENT,
};
